/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { FormContext } from '@shared/types';
import { useFormStore } from "./FormStore";
import { scrollToFirstMissingField } from "@/shared/utils/scroll-to-field";

type FormSubmitBehaviorProps = {
  onSubmit: (formStore: FormContext) => void;
  onFallback?: (formStore: FormContext) => void;
  clearAfterSubmit?: boolean;
  noValidate?: boolean;
};
/**
 * shouldSubmitForm function.
 *
 * @param {*} isValid - Parameter description
 * @param {*} noValidate - Parameter description
 *
 * @returns {*} Return value description
 */
function shouldSubmitForm(isValid: boolean, noValidate: boolean): boolean {
  return isValid || noValidate;
}

/**
 * executeSubmit function.
 *
 * @param {*} onSubmit - Parameter description
 * @param {*} get - Parameter description
 * @param {*} clearAfterSubmit - Parameter description
 * @param {*} clearForm - Parameter description
 *
 * @returns {*} Return value description
 */
function executeSubmit(
/**
 * onSubmit function.
 *
 * @param {*} formStore - Parameter description
 *
 * @returns {*} Return value description
 */
  onSubmit: (formStore: FormContext) => void,
/**
 * get function.
 *
 * @returns {*} Return value description
 */
  get: () => FormContext,
  clearAfterSubmit: boolean,
/**
 * clearForm function.
 *
 * @returns {*} Return value description
 */
  clearForm: () => void
) {
  onSubmit(get());
  if (clearAfterSubmit) {
    clearForm();
  }
}

/**
 * executeFallback function.
 *
 * @param {*} onFallback - Parameter description
 * @param {*} get - Parameter description
 *
 * @returns {*} Return value description
 */
function executeFallback(
/**
 * onFallback function.
 *
 * @param {*} (formStore - Parameter description
 *
 * @returns {*} Return value description
 */
  onFallback: ((formStore: FormContext) => void) | undefined,
/**
 * get function.
 *
 * @returns {*} Return value description
 */
  get: () => FormContext
) {
  onFallback?.(get());
}

/**
 * Handles form submission logic.
 *
 * @param {boolean} isValid - Whether form is valid
 * @param {boolean} noValidate - Whether to skip validation
 * @param {Function} onSubmit - Submit handler
 * @param {Function} onFallback - Fallback handler
 * @param {Function} get - Function to get form context
 * @param {boolean} clearAfterSubmit - Whether to clear form after submit
 * @param {Function} clearForm - Function to clear form
 * @returns {void}
 */
function handleFormSubmit(
  isValid: boolean,
  noValidate: boolean,
  onSubmit: (formStore: FormContext) => void,
  onFallback: ((formStore: FormContext) => void) | undefined,
  get: () => FormContext,
  clearAfterSubmit: boolean,
  clearForm: () => void
): void {
  if (shouldSubmitForm(isValid, noValidate)) {
    executeSubmit(onSubmit, get, clearAfterSubmit, clearForm);
    return;
  }
  executeFallback(onFallback, get);
}

const FormSubmitBehavior: ComponentWithChildren<FormSubmitBehaviorProps> = ({
  onSubmit,
  onFallback,
  clearAfterSubmit = false,
  noValidate = false,
  children,
}) => {
  const get = useFormStore((state) => state.get);
  const validate = useFormStore((state) => state.validate);
  const clearForm = useFormStore((state) => state.clearForm);
  const getFirstMissingFieldForScroll = useFormStore((state) => state.getFirstMissingFieldForScroll);
  
  const handleClick = () => {
    const isValid = validate();
    
    if (!isValid && !noValidate) {
      // Get first missing field and scroll to it
      const firstMissingField = getFirstMissingFieldForScroll?.();
      if (firstMissingField) {
        // Use setTimeout to ensure DOM is updated with error states
        setTimeout(() => {
          scrollToFirstMissingField(firstMissingField);
        }, 50);
      }
    }
    
    handleFormSubmit(isValid, noValidate, onSubmit, onFallback, get, clearAfterSubmit, clearForm);
  };
  
  return (
    <div
      style={{ display: "contents" }}
      data-form-submit="true"
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

export default FormSubmitBehavior;
